import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import Slider from "react-slick";
import { getCountdown } from "../helper/Countdown";
import { getProductDetails, apiRequest } from "../apiService/allapi";
import { successAlert, errorAlert } from "../apiService/sweetAlert";
import {
  addToGuestCart,
  isLoggedIn,
  dispatchCartUpdate,
  isProductInCart,
} from "../helper/cartUtils";

const ProductDetails = ({ productId }) => {
  const [timeLeft, setTimeLeft] = useState(getCountdown());
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [quantity, setQuantity] = useState(1);
  const [wishlistProducts, setWishlistProducts] = useState(new Set()); // 🔥 Track by product ID

  const navigate = useNavigate();
  const discount = product?.discount || 0;
  useEffect(() => {
    const interval = setInterval(() => {
      setTimeLeft(getCountdown());
    }, 1000);

    return () => clearInterval(interval);
  }, []);

  // Fetch product details when productId changes
  useEffect(() => {
    const fetchProductDetails = async () => {
      if (!productId) {
        setError("Product ID not found");
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const response = await getProductDetails(productId);
        // API returns { status: 1, data: [...] } - get first item from data array
        if (
          response &&
          response.data &&
          response.data.data &&
          response.data.data.length > 0
        ) {
          setProduct(response.data.data[0]);
        } else {
          setError("Product not found");
        }
      } catch (err) {
        console.error("Error fetching product:", err);
        setError("Failed to load product details");
      } finally {
        setLoading(false);
      }
    };

    fetchProductDetails();
  }, [productId]);

  // Get product images from API response
  const BASE_URL = process.env.REACT_APP_IMAGE_BASE_URL;

  const getProductImages = () => {
    if (!product) return [];

    const images = [];

    if (product.imgurl) images.push(BASE_URL + product.imgurl);
    if (product.imG1URL) images.push(BASE_URL + product.imG1URL);
    if (product.imG2URL) images.push(BASE_URL + product.imG2URL);
    if (product.imG3URL) images.push(BASE_URL + product.imG3URL);
    if (product.imG4URL) images.push(BASE_URL + product.imG4URL);
    if (product.imG5URL) images.push(BASE_URL + product.imG5URL);

    return images;
  };

  const productImages = getProductImages();

  const [mainImage, setMainImage] = useState(productImages[0]);

  // Update mainImage when product images change
  useEffect(() => {
    if (productImages) {
      setMainImage(productImages[0]);
    }
  }, [productImages]);

  // 🔥 Wishlist functions (before useEffect to avoid hoisting issues)
  const addToWishlist = async (pid) => {
    try {
      const token = localStorage.getItem("token");

      const res = await apiRequest(
        "/Wishlist/AddToggleWishlist",
        "POST",
        { productId: pid },
        { Authorization: `Bearer ${token}` },
      );

      console.log("Wishlist API Response:", res);

      if (res?.data?.success === true || res?.data?.status === true) {
        successAlert(res.data.message);
        window.dispatchEvent(new Event("wishlistUpdated"));

        setWishlistProducts((prev) => {
          const updated = new Set(prev);

          if (updated.has(pid)) {
            updated.delete(pid); // remove
          } else {
            updated.add(pid); // add
          }

          return updated;
        });
      } else {
        errorAlert(res?.data?.message || "Failed");
      }
    } catch (error) {
      console.log("Wishlist Error:", error);
      errorAlert("Something went wrong");
    }
  };

  // Load initial wishlist from server
  const getWishlistItems = async () => {
    try {
      const token = localStorage.getItem("token");
      if (!token) return; // Guest: empty wishlist

      const res = await apiRequest("/Wishlist/getToggle", "GET", null, {
        Authorization: `Bearer ${token}`,
      });

      console.log("Initial Wishlist:", res);

      if (res?.data?.status === true) {
        const wishlistPids = new Set(
          (res.data.data || []).map((item) => item.pid),
        );
        setWishlistProducts(wishlistPids);
      }
    } catch (error) {
      console.log("Load wishlist error:", error);
    }
  };

  // Load wishlist on product change
  useEffect(() => {
    getWishlistItems();
  }, [productId]);

  const settingsThumbs = {
    dots: false,
    infinite: true,
    speed: 500,
    slidesToShow: 4,
    slidesToScroll: 1,
    focusOnSelect: true,
  arrows: false, // ❌ hide arrows

  };

  // increment & decrement with stock limit
  const incrementQuantity = () => {
    const newQty = quantity + 1;
    const maxStock = product?.stock || product?.stockQty || 0;
    setQuantity(Math.min(newQty, maxStock));
  };
  const decrementQuantity = () => setQuantity(quantity > 1 ? quantity - 1 : 1);

  if (loading) {
    return (
      <section className="product-details py-80">
        <div className="container container-lg">
          <div className="text-center">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
          </div>
        </div>
      </section>
    );
  }

  if (error) {
    return (
      <section className="product-details py-80">
        <div className="container container-lg">
          <div className="text-center">
            <h4 className="text-danger">{error}</h4>
            <Link to="/shop" className="btn btn-main rounded-pill mt-3">
              Back to Shop
            </Link>
          </div>
        </div>
      </section>
    );
  }

  // Updated add to cart - single item only policy
  const addToCart = async () => {
    try {
      const pid = product?.id || product?.pid;

      const cart = JSON.parse(localStorage.getItem("guestCart")) || [];
      const item = cart.find((i) => i.pid === pid);
      const existingQty = item ? item.qty : 0;

      const availableStock = product?.stock || 0;

      if (existingQty + quantity > availableStock) {
        errorAlert("Out of Stock");
        return;
      }

      const payload = {
        pid: pid,
        qty: quantity,
      };

      if (isLoggedIn()) {
        const token = localStorage.getItem("token");

        const res = await apiRequest("/Cart/add-to-cart", "POST", payload, {
          Authorization: `Bearer ${token}`,
        });

        if (res?.data?.status === true) {
          successAlert("Added to cart!");
          dispatchCartUpdate();
          navigate("/cart");
        } else {
          errorAlert(res.data.message);
        }
      } else {
        const guestItem = {
          pid: payload.pid,
          productName: product.productName,
          sellingPrice: product.sellingPrice,
          imG1: product.imG1URL || product.imgurl || "",
          qty: quantity,
        };

        addToGuestCart(guestItem);
        dispatchCartUpdate();
        navigate("/cart");
      }
    } catch (error) {
      console.log(error);
      errorAlert("Failed to add to cart");
    }
  };

  return (
    <section className="product-details py-80">
      <div className="container container-lg">
        <div className="row gy-4">
          <div className="col-lg-9">
            <div className="row gy-4">
              <div className="col-xl-6">
                <div className="product-details__left">
                  <div className="product-details__thumb-slider border border-gray-100 rounded-16">
                    <div className="">
                      <div className="product-details__thumb flex-center h-100">
                        <img
                          src={mainImage}
                          alt={product?.productName || "Main Product"}
                        />
                      </div>
                    </div>
                  </div>
                  <div className="mt-24">
                    <div className="product-details__images-slider">
                      <Slider {...settingsThumbs}>
                        {productImages.map((image, index) => (
                          <div
                            className="centerw-[180px] h-[180px] flex items-center justify-center border border-gray-100 rounded-16 p-2 "
                            key={index}
                            onClick={() => setMainImage(image)}
                          >
                            <img
                              className="thum"
                              src={image}
                              alt={`Thumbnail ${index}`}
                            />
                          </div>
                        ))}
                      </Slider>
                    </div>
                  </div>
                </div>
              </div>
              <div className="col-xl-6">
                <div className="product-details__content">
                  <h5 className="mb-12">
                    {product?.productName || "Product Name"}
                  </h5>
                  {/* Seller Info */}
                  {product?.sellerName && (
                    <div className="mb-8">
                      <span className="text-sm text-gray-600">
                        <span className="text-gray-400">Sold by:</span>{" "}
                        {product.sellerName}
                        {product?.sellerType && (
                          <span className="text-gray-500">
                            {" "}
                            ({product.sellerType})
                          </span>
                        )}
                      </span>
                    </div>
                  )}
                  <div className="flex-align flex-wrap gap-12">
                    <div className="flex-align gap-12 flex-wrap">
                      <div className="flex-align gap-8">
                        <span className="text-15 fw-medium text-warning-600 d-flex">
                          <i className="ph-fill ph-star" />
                        </span>
                        <span className="text-15 fw-medium text-warning-600 d-flex">
                          <i className="ph-fill ph-star" />
                        </span>
                        <span className="text-15 fw-medium text-warning-600 d-flex">
                          <i className="ph-fill ph-star" />
                        </span>
                        <span className="text-15 fw-medium text-warning-600 d-flex">
                          <i className="ph-fill ph-star" />
                        </span>
                        <span className="text-15 fw-medium text-warning-600 d-flex">
                          <i className="ph-fill ph-star" />
                        </span>
                      </div>
                      <span className="text-sm fw-medium text-neutral-600">
                        {product?.ratingAverage || "4.7"} Star Rating
                      </span>
                      <span className="text-sm fw-medium text-gray-500">
                        ({product?.totalRating || "(21,671)"})
                      </span>
                    </div>
                    <span className="text-sm fw-medium text-gray-500">|</span>
                    <span className="text-gray-900">
                      {" "}
                      <span className="text-gray-400">SKU:</span>
                      {product?.skuid || "EB4DRP"}{" "}
                    </span>
                  </div>
                  <span className="mt-32 pt-32 text-gray-700 border-top border-gray-100 d-block" />
                  <p className="text-gray-700">
                    {product?.description ||
                      "Vivamus adipiscing nisl ut dolor dignissim semper. Nulla luctus malesuada tincidunt."}
                  </p>

                  <div className="mt-32 flex-align flex-wrap gap-32">
                    <div className="flex-align gap-8">
                      <h4 className="mb-0">
                        ₹{product?.sellingPrice || "25.00"}
                      </h4>
                      <span className="text-md text-gray-500">
                        ₹{product?.mrp || "38.00"}
                      </span>
                      {discount > 0 && (
                        <span className="text-sm text-success-600 fw-bold">
                          {discount}% OFF
                        </span>
                      )}
                    </div>
                    {/* Discount Amount */}
                    {product?.discountAmt > 0 && (
                      <div className="mt-8">
                        <span className="text-sm text-danger-600">
                          Save ₹{product.discountAmt}
                        </span>
                      </div>
                    )}
                  </div>

                  {/* Tax Information */}
                  <div className="mt-16 flex-align gap-16 flex-wrap">
                    <span className="text-sm text-gray-600">
                      <span className="text-gray-400">CGST:</span>{" "}
                      {product?.cgst || "0"}%
                    </span>
                    <span className="text-sm text-gray-600">
                      <span className="text-gray-400">SGST:</span>{" "}
                      {product?.sgst || "0"}%
                    </span>
                    <span className="text-sm text-gray-600">
                      <span className="text-gray-400">IGST:</span>{" "}
                      {product?.igst || "0"}%
                    </span>
                  </div>

                  {/* Shipping Charges */}
                  {Number(product?.shippingCharges) > 0 && (
                    <div className="mt-8">
                      <span className="text-sm text-gray-600">
                        <i className="ph-fill ph-truck text-main-600 me-1"></i>
                        Shipping: ₹{product.shippingCharges}
                      </span>
                    </div>
                  )}
                  <span className="mt-32 pt-32 text-gray-700 border-top border-gray-100 d-block" />
                  <div className="mb-24">
                    <div className="mt-32 flex-align gap-12 mb-16">
                      <span className="w-32 h-32 bg-white flex-center rounded-circle text-main-600 box-shadow-xl">
                        <i className="ph-fill ph-lightning" />
                      </span>
                      <h6 className="text-md mb-0 fw-bold text-gray-900">
                        Products are almost sold out
                      </h6>
                    </div>
                    <div
                      className="progress w-100 bg-gray-100 rounded-pill h-8"
                      role="progressbar"
                      aria-label="Basic example"
                      aria-valuenow={32}
                      aria-valuemin={0}
                      aria-valuemax={100}
                    >
                      <div
                        className="progress-bar bg-main-two-600 rounded-pill"
                        style={{ width: "32%" }}
                      />
                    </div>
                    <span className="text-sm text-gray-700 mt-8">
                      Available only:{product?.stock || 45}
                    </span>
                  </div>
                  <span className="text-gray-900 d-block mb-8">Quantity:</span>
                  <div className="flex-between gap-16 flex-wrap">
                    <div className="flex-align flex-wrap gap-16">
                      <div className="border border-gray-100 rounded-pill py-9 px-16 flex-align">
                        <button
                          onClick={decrementQuantity}
                          type="button"
                          className="quantity__minus p-4 text-gray-700 hover-text-main-600 flex-center"
                        >
                          <i className="ph ph-minus" />
                        </button>
                        <input
                          type="number"
                          className="quantity__input border-0 text-center w-32"
                          value={quantity}
                          readOnly
                        />
                        <button
                          onClick={incrementQuantity}
                          type="button"
                          className="quantity__plus p-4 text-gray-700 hover-text-main-600 flex-center"
                        >
                          <i className="ph ph-plus" />
                        </button>
                      </div>

                      {/* ✅ STOCK CONDITION */}
                      {(product?.stock || 0) > 0 && (
                        <button
                          onClick={addToCart}
                          className="btn btn-main rounded-pill flex-align d-inline-flex gap-8 px-48"
                        >
                          <i className="ph ph-shopping-cart" /> Add To Cart
                        </button>
                      )}
                    </div>
                    {/* Stock Information */}
                    {product?.stock !== undefined && (
                      <div className="mt-12">
                        {product.stock > 0 ? (
                          <span className="text-sm text-success-600 flex-align gap-4">
                            <i className="ph-fill ph-check-circle"></i>
                            In Stock ({product.stock} available)
                          </span>
                        ) : (
                          <span className="text-sm text-danger-600 flex-align gap-4">
                            <i className="ph-fill ph-x-circle"></i>
                            Out of Stock
                          </span>
                        )}
                      </div>
                    )}
                    <div className="flex-align gap-12">
                      {isLoggedIn() && (
                      <button
                        onClick={() => addToWishlist(product.id)}
                        className="w-52 h-52 bg-main-50 text-main-600 text-xl hover-bg-main-600 hover-text-white flex-center rounded-circle border-0"
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="20"
                          height="20"
                          viewBox="0 0 24 24"
                          fill={
                            wishlistProducts.has(product.id)
                              ? "#ef4444"
                              : "none"
                          }
                          stroke={
                            wishlistProducts.has(product.id)
                              ? "#ef4444"
                              : "currentColor"
                          }
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          className="transition-colors duration-200"
                        >
                          <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"></path>
                        </svg>
                      </button>
                      )}
                      <Link
                        to="#"
                        className="w-52 h-52 bg-main-50 text-main-600 text-xl hover-bg-main-600 hover-text-white flex-center rounded-circle"
                      >
                        <i className="ph ph-shuffle" />
                      </Link>
                      <Link
                        to="#"
                        className="w-52 h-52 bg-main-50 text-main-600 text-xl hover-bg-main-600 hover-text-white flex-center rounded-circle"
                      >
                        <i className="ph ph-share-network" />
                      </Link>
                    </div>
                  </div>
                  <span className="mt-32 pt-32 text-gray-700 border-top border-gray-100 d-block" />
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="pt-80">
          <div className="product-dContent border rounded-24">
            <div className="product-dContent__header border-bottom border-gray-100 flex-between flex-wrap gap-16">
              <ul
                className="nav common-tab nav-pills mb-3"
                id="pills-tab"
                role="tablist"
              >
                <li className="nav-item" role="presentation">
                  <button
                    className="nav-link active"
                    id="pills-description-tab"
                    data-bs-toggle="pill"
                    data-bs-target="#pills-description"
                    type="button"
                    role="tab"
                    aria-controls="pills-description"
                    aria-selected="true"
                  >
                    Description
                  </button>
                </li>
                <li className="nav-item" role="presentation">
                  <button
                    className="nav-link"
                    id="pills-reviews-tab"
                    data-bs-toggle="pill"
                    data-bs-target="#pills-reviews"
                    type="button"
                    role="tab"
                    aria-controls="pills-reviews"
                    aria-selected="false"
                  >
                    Reviews
                  </button>
                </li>
              </ul>
              <Link
                to="#"
                className="btn bg-color-one rounded-16 flex-align gap-8 text-main-600 hover-bg-main-600 hover-text-white"
              >
                <img src="assets/images/icon/satisfaction-icon.png" alt="" />
                100% Satisfaction Guaranteed
              </Link>
            </div>
            <div className="product-dContent__box">
              <div className="tab-content" id="pills-tabContent">
                <div
                  className="tab-pane fade show active"
                  id="pills-description"
                  role="tabpanel"
                  aria-labelledby="pills-description-tab"
                  tabIndex={0}
                >
                  <div className="mb-40">
                    <h6 className="mb-24">Product Description</h6>
                    <p>
                      {product?.description ||
                        "Whenever celebrations and good times happen, the LAY'S brand will be there just as it has been for more than 75 years. With flavors almost as rich as our history, we have a chip or crisp flavor guaranteed to bring a smile on your face."}
                    </p>
                  </div>
                </div>
                <div
                  className="tab-pane fade"
                  id="pills-reviews"
                  role="tabpanel"
                  aria-labelledby="pills-reviews-tab"
                  tabIndex={0}
                >
                  {/* <div className="row g-4">
                    <div className="col-lg-6">
                      <h6 className="mb-24">Product Description</h6>
                      <div className="d-flex align-items-start gap-24 pb-44 border-bottom border-gray-100 mb-44">
                        <img
                          src="assets/images/thumbs/comment-img1.png"
                          alt=""
                          className="w-52 h-52 object-fit-cover rounded-circle flex-shrink-0"
                        />
                        <div className="flex-grow-1">
                          <div className="flex-between align-items-start gap-8 ">
                            <div className="">
                              <h6 className="mb-12 text-md">Nicolas cage</h6>
                              <div className="flex-align gap-8">
                                <span className="text-15 fw-medium text-warning-600 d-flex">
                                  <i className="ph-fill ph-star" />
                                </span>
                                <span className="text-15 fw-medium text-warning-600 d-flex">
                                  <i className="ph-fill ph-star" />
                                </span>
                                <span className="text-15 fw-medium text-warning-600 d-flex">
                                  <i className="ph-fill ph-star" />
                                </span>
                                <span className="text-15 fw-medium text-warning-600 d-flex">
                                  <i className="ph-fill ph-star" />
                                </span>
                                <span className="text-15 fw-medium text-warning-600 d-flex">
                                  <i className="ph-fill ph-star" />
                                </span>
                              </div>
                            </div>
                            <span className="text-gray-800 text-xs">
                              3 Days ago
                            </span>
                          </div>
                          <h6 className="mb-14 text-md mt-24">
                            Greate Product
                          </h6>
                          <p className="text-gray-700">
                            There are many variations of passages of Lorem Ipsum
                            available, but the majority have suffered alteration
                            in some form, by injected humour
                          </p>
                          <div className="flex-align gap-20 mt-44">
                            <button className="flex-align gap-12 text-gray-700 hover-text-main-600">
                              <i className="ph-bold ph-thumbs-up" />
                              Like
                            </button>
                            <Link
                              to="#comment-form"
                              className="flex-align gap-12 text-gray-700 hover-text-main-600"
                            >
                              <i className="ph-bold ph-arrow-bend-up-left" />
                              Replay
                            </Link>
                          </div>
                        </div>
                      </div>
                      <div className="mt-56">
                        <div className="">
                          <h6 className="mb-24">Write a Review</h6>
                          <span className="text-heading mb-8">
                            What is it like to Product?
                          </span>
                          <div className="flex-align gap-8">
                            <span className="text-15 fw-medium text-warning-600 d-flex">
                              <i className="ph-fill ph-star" />
                            </span>
                            <span className="text-15 fw-medium text-warning-600 d-flex">
                              <i className="ph-fill ph-star" />
                            </span>
                            <span className="text-15 fw-medium text-warning-600 d-flex">
                              <i className="ph-fill ph-star" />
                            </span>
                            <span className="text-15 fw-medium text-warning-600 d-flex">
                              <i className="ph-fill ph-star" />
                            </span>
                            <span className="text-15 fw-medium text-warning-600 d-flex">
                              <i className="ph-fill ph-star" />
                            </span>
                          </div>
                        </div>
                        <div className="mt-32">
                          <form action="#">
                            <div className="mb-32">
                              <label
                                htmlFor="title"
                                className="text-neutral-600 mb-8"
                              >
                                Review Title
                              </label>
                              <input
                                type="text"
                                className="common-input rounded-8"
                                id="title"
                                placeholder="Great Products"
                              />
                            </div>
                            <div className="mb-32">
                              <label
                                htmlFor="desc"
                                className="text-neutral-600 mb-8"
                              >
                                Review Content
                              </label>
                              <textarea
                                className="common-input rounded-8"
                                id="desc"
                                defaultValue={
                                  "It is a long established fact that a reader will be distracted by the readable content of a page when looking at its layout. The point of using Lorem Ipsum is that it has a more-or-less normal distribution of letters, as opposed to using 'Content here, content here', making it look like readable English."
                                }
                              />
                            </div>
                            <button
                              type="submit"
                              className="btn btn-main rounded-pill mt-48"
                            >
                              Submit Review
                            </button>
                          </form>
                        </div>
                      </div>
                    </div>
                    <div className="col-lg-6">
                      <div className="ms-xxl-5">
                        <h6 className="mb-24">Customers Feedback</h6>
                        <div className="d-flex flex-wrap gap-44">
                          <div className="border border-gray-100 rounded-8 px-40 py-52 flex-center flex-column flex-shrink-0 text-center">
                            <h2 className="mb-6 text-main-600">
                              {product?.ratingAverage || "4.8"}
                            </h2>
                            <div className="flex-center gap-8">
                              <span className="text-15 fw-medium text-warning-600 d-flex">
                                <i className="ph-fill ph-star" />
                              </span>
                              <span className="text-15 fw-medium text-warning-600 d-flex">
                                <i className="ph-fill ph-star" />
                              </span>
                              <span className="text-15 fw-medium text-warning-600 d-flex">
                                <i className="ph-fill ph-star" />
                              </span>
                              <span className="text-15 fw-medium text-warning-600 d-flex">
                                <i className="ph-fill ph-star" />
                              </span>
                              <span className="text-15 fw-medium text-warning-600 d-flex">
                                <i className="ph-fill ph-star" />
                              </span>
                            </div>
                            <span className="mt-16 text-gray-500">
                              Average Product Rating
                            </span>
                          </div>
                          <div className="border border-gray-100 rounded-8 px-24 py-40 flex-grow-1">
                            <div className="flex-align gap-8 mb-20">
                              <span className="text-gray-900 flex-shrink-0">
                                5
                              </span>
                              <div
                                className="progress w-100 bg-gray-100 rounded-pill h-8"
                                role="progressbar"
                                aria-label="Basic example"
                                aria-valuenow={70}
                                aria-valuemin={0}
                                aria-valuemax={100}
                              >
                                <div
                                  className="progress-bar bg-main-600 rounded-pill"
                                  style={{ width: "70%" }}
                                />
                              </div>
                              <div className="flex-align gap-4">
                                <span className="text-xs fw-medium text-warning-600 d-flex">
                                  <i className="ph-fill ph-star" />
                                </span>
                                <span className="text-xs fw-medium text-warning-600 d-flex">
                                  <i className="ph-fill ph-star" />
                                </span>
                                <span className="text-xs fw-medium text-warning-600 d-flex">
                                  <i className="ph-fill ph-star" />
                                </span>
                                <span className="text-xs fw-medium text-warning-600 d-flex">
                                  <i className="ph-fill ph-star" />
                                </span>
                                <span className="text-xs fw-medium text-warning-600 d-flex">
                                  <i className="ph-fill ph-star" />
                                </span>
                              </div>
                              <span className="text-gray-900 flex-shrink-0">
                                124
                              </span>
                            </div>
                            <div className="flex-align gap-8 mb-20">
                              <span className="text-gray-900 flex-shrink-0">
                                4
                              </span>
                              <div
                                className="progress w-100 bg-gray-100 rounded-pill h-8"
                                role="progressbar"
                                aria-label="Basic example"
                                aria-valuenow={50}
                                aria-valuemin={0}
                                aria-valuemax={100}
                              >
                                <div
                                  className="progress-bar bg-main-600 rounded-pill"
                                  style={{ width: "50%" }}
                                />
                              </div>
                              <div className="flex-align gap-4">
                                <span className="text-xs fw-medium text-warning-600 d-flex">
                                  <i className="ph-fill ph-star" />
                                </span>
                                <span className="text-xs fw-medium text-warning-600 d-flex">
                                  <i className="ph-fill ph-star" />
                                </span>
                                <span className="text-xs fw-medium text-warning-600 d-flex">
                                  <i className="ph-fill ph-star" />
                                </span>
                                <span className="text-xs fw-medium text-warning-600 d-flex">
                                  <i className="ph-fill ph-star" />
                                </span>
                                <span className="text-xs fw-medium text-warning-600 d-flex">
                                  <i className="ph-fill ph-star" />
                                </span>
                              </div>
                              <span className="text-gray-900 flex-shrink-0">
                                52
                              </span>
                            </div>
                            <div className="flex-align gap-8 mb-20">
                              <span className="text-gray-900 flex-shrink-0">
                                3
                              </span>
                              <div
                                className="progress w-100 bg-gray-100 rounded-pill h-8"
                                role="progressbar"
                                aria-label="Basic example"
                                aria-valuenow={35}
                                aria-valuemin={0}
                                aria-valuemax={100}
                              >
                                <div
                                  className="progress-bar bg-main-600 rounded-pill"
                                  style={{ width: "35%" }}
                                />
                              </div>
                              <div className="flex-align gap-4">
                                <span className="text-xs fw-medium text-warning-600 d-flex">
                                  <i className="ph-fill ph-star" />
                                </span>
                                <span className="text-xs fw-medium text-warning-600 d-flex">
                                  <i className="ph-fill ph-star" />
                                </span>
                                <span className="text-xs fw-medium text-warning-600 d-flex">
                                  <i className="ph-fill ph-star" />
                                </span>
                                <span className="text-xs fw-medium text-warning-600 d-flex">
                                  <i className="ph-fill ph-star" />
                                </span>
                                <span className="text-xs fw-medium text-warning-600 d-flex">
                                  <i className="ph-fill ph-star" />
                                </span>
                              </div>
                              <span className="text-gray-900 flex-shrink-0">
                                12
                              </span>
                            </div>
                            <div className="flex-align gap-8 mb-20">
                              <span className="text-gray-900 flex-shrink-0">
                                2
                              </span>
                              <div
                                className="progress w-100 bg-gray-100 rounded-pill h-8"
                                role="progressbar"
                                aria-label="Basic example"
                                aria-valuenow={20}
                                aria-valuemin={0}
                                aria-valuemax={100}
                              >
                                <div
                                  className="progress-bar bg-main-600 rounded-pill"
                                  style={{ width: "20%" }}
                                />
                              </div>
                              <div className="flex-align gap-4">
                                <span className="text-xs fw-medium text-warning-600 d-flex">
                                  <i className="ph-fill ph-star" />
                                </span>
                                <span className="text-xs fw-medium text-warning-600 d-flex">
                                  <i className="ph-fill ph-star" />
                                </span>
                                <span className="text-xs fw-medium text-warning-600 d-flex">
                                  <i className="ph-fill ph-star" />
                                </span>
                                <span className="text-xs fw-medium text-warning-600 d-flex">
                                  <i className="ph-fill ph-star" />
                                </span>
                                <span className="text-xs fw-medium text-warning-600 d-flex">
                                  <i className="ph-fill ph-star" />
                                </span>
                              </div>
                              <span className="text-gray-900 flex-shrink-0">
                                5
                              </span>
                            </div>
                            <div className="flex-align gap-8 mb-0">
                              <span className="text-gray-900 flex-shrink-0">
                                1
                              </span>
                              <div
                                className="progress w-100 bg-gray-100 rounded-pill h-8"
                                role="progressbar"
                                aria-label="Basic example"
                                aria-valuenow={5}
                                aria-valuemin={0}
                                aria-valuemax={100}
                              >
                                <div
                                  className="progress-bar bg-main-600 rounded-pill"
                                  style={{ width: "5%" }}
                                />
                              </div>
                              <div className="flex-align gap-4">
                                <span className="text-xs fw-medium text-warning-600 d-flex">
                                  <i className="ph-fill ph-star" />
                                </span>
                                <span className="text-xs fw-medium text-warning-600 d-flex">
                                  <i className="ph-fill ph-star" />
                                </span>
                                <span className="text-xs fw-medium text-warning-600 d-flex">
                                  <i className="ph-fill ph-star" />
                                </span>
                                <span className="text-xs fw-medium text-warning-600 d-flex">
                                  <i className="ph-fill ph-star" />
                                </span>
                                <span className="text-xs fw-medium text-warning-600 d-flex">
                                  <i className="ph-fill ph-star" />
                                </span>
                              </div>
                              <span className="text-gray-900 flex-shrink-0">
                                2
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div> */}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ProductDetails;
